import { useQuery } from '@tanstack/react-query';
import { getConfig, getHealth } from '@/api/config';
import { useAuth } from '@/auth/useAuth';
import { formatDateTime } from '@/lib/dateFormat';

/**
 * Settings — Stage 1 read-only diagnostics page.
 *
 * Surfaces the env-var reflector (GET /api/config) and the dependency probe
 * (GET /api/healthz) so an admin can confirm the tenant is wired up after
 * provisioning, without leaking any secret values. Connectivity is shown as
 * coloured badges; no secret is ever displayed — only a configured / not
 * configured flag.
 *
 * Admin-only management of reference data, stock locations, asset-tag
 * configuration and sync-job status arrives in Stage 7.
 */
export function Settings() {
  const { displayName } = useAuth();

  const configQuery = useQuery({ queryKey: ['config'], queryFn: ({ signal }) => getConfig(signal) });
  const healthQuery = useQuery({ queryKey: ['health'], queryFn: ({ signal }) => getHealth(signal) });

  return (
    <section className="max-w-3xl">
      <header className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-navy">Settings</h1>
        <p className="mt-1 text-sm text-darkGrey">
          Tenant configuration and connectivity{displayName ? ` — signed in as ${displayName}` : ''}.
        </p>
      </header>

      <div className="rounded border border-border bg-white p-5 shadow-sm">
        <h2 className="font-heading text-base font-semibold text-navy">Integrations</h2>
        {configQuery.isLoading && (
          <p className="mt-3 text-sm text-darkGrey/70">Loading configuration…</p>
        )}
        {configQuery.isError && (
          <p className="mt-3 text-sm text-red">Could not load configuration.</p>
        )}
        {configQuery.data && (
          <dl className="mt-3 grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
            <Row label="Sender email" value={configQuery.data.OscarSenderEmail} />
            <Row label="Notification recipient" value={configQuery.data.OscarEmail ?? 'Not set'} />
            <BadgeRow label="SMTP2GO" ok={configQuery.data.Smtp2goConfigured} />
            <BadgeRow label="Microsoft Graph" ok={configQuery.data.GraphConfigured} />
            <BadgeRow label="Dell TechDirect" ok={configQuery.data.DellConfigured} />
          </dl>
        )}
      </div>

      <div className="mt-4 rounded border border-border bg-white p-5 shadow-sm">
        <h2 className="font-heading text-base font-semibold text-navy">Health</h2>
        {healthQuery.isLoading && <p className="mt-3 text-sm text-darkGrey/70">Checking…</p>}
        {healthQuery.isError && <p className="mt-3 text-sm text-red">Health probe failed.</p>}
        {healthQuery.data && (
          <dl className="mt-3 grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
            <StatusRow label="Overall" value={healthQuery.data.Status} good={healthQuery.data.Status === 'ok'} />
            <StatusRow label="SQL (OnsiteCore)" value={healthQuery.data.Sql} good={healthQuery.data.Sql === 'ok'} />
            <StatusRow label="Graph" value={healthQuery.data.Graph} good={healthQuery.data.Graph === 'ok' || healthQuery.data.Graph === 'configured'} />
            <Row label="Checked" value={formatDateTime(healthQuery.data.Timestamp)} />
          </dl>
        )}
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-44 shrink-0 font-semibold text-darkGrey">{label}</span>
      <span className="text-darkGrey/80">{value}</span>
    </div>
  );
}

function BadgeRow({ label, ok, okLabel = 'Configured', offLabel = 'Not configured' }: { label: string; ok: boolean; okLabel?: string; offLabel?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-44 shrink-0 font-semibold text-darkGrey">{label}</span>
      <span
        className={
          ok
            ? 'inline-flex items-center rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green'
            : 'inline-flex items-center rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey/70'
        }
      >
        {ok ? okLabel : offLabel}
      </span>
    </div>
  );
}

function StatusRow({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-44 shrink-0 font-semibold text-darkGrey">{label}</span>
      <span
        className={
          good
            ? 'inline-flex items-center rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green'
            : 'inline-flex items-center rounded-full bg-orange/15 px-2 py-0.5 text-xs font-semibold text-orange'
        }
      >
        {value}
      </span>
    </div>
  );
}
